import React, { useState, useEffect } from 'react';
import { FiHeart, FiBook, FiUsers, FiCalendar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Stats = () => {
  const [counters, setCounters] = useState([0, 0, 0, 0]);
  const [hasAnimated, setHasAnimated] = useState(false);

  const stats = [
    { 
      icon: FiHeart, 
      value: 50, 
      label: "Happy Learners Enrolled", 
      suffix: "+",
      color: "from-pink-400 to-rose-500"
    },
    { 
      icon: FiBook, 
      value: 20, 
      label: "Interactive Learning Modules", 
      suffix: "+",
      color: "from-blue-400 to-indigo-500"
    },
    { 
      icon: FiUsers, 
      value: 8, 
      label: "Trained & Caring Educators", 
      suffix: "+",
      color: "from-emerald-400 to-teal-500"
    },
    { 
      icon: FiCalendar, 
      value: 12, 
      label: "Fun Events Conducted", 
      suffix: "+",
      color: "from-orange-400 to-red-500"
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const statsSection = document.getElementById('stats');
      if (statsSection && !hasAnimated) {
        const rect = statsSection.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isVisible) {
          setHasAnimated(true);
          
          stats.forEach((stat, index) => {
            let current = 0;
            const increment = stat.value / 50;
            const timer = setInterval(() => {
              current += increment;
              if (current >= stat.value) {
                current = stat.value;
                clearInterval(timer);
              }
              setCounters(prev => {
                const newCounters = [...prev];
                newCounters[index] = Math.floor(current);
                return newCounters;
              });
            }, 40);
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasAnimated]);

  return (
    <section id="stats" className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
              Achievements
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Numbers that reflect our commitment to providing exceptional early childhood education 
            and building a strong foundation for every child's future.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className="text-center group"
              data-aos="zoom-in"
              data-aos-delay={index * 100}
            >
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                {/* Icon */}
                <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${stat.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={stat.icon} className="w-10 h-10 text-white" />
                </div>

                {/* Counter */}
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors duration-300">
                    {counters[index]}{stat.suffix}
                  </span>
                </div>

                {/* Label */}
                <p className="text-gray-600 font-medium leading-relaxed">
                  {stat.label}
                </p>

                {/* Hover Effect */}
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className={`w-full h-1 bg-gradient-to-r ${stat.color} rounded-full`}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Message */}
        <div className="text-center mt-16" data-aos="fade-up" data-aos-delay="500">
          <p className="text-lg text-gray-600 mb-6">
            Join our growing family of happy learners and caring educators
          </p>
          <a
            href="tel:+917416706111"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <span>Be Part of Our Success Story</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Stats;